import { attr, controller, target, targets } from "@github/catalyst"

// The broadcast composer (BroadcastComposerComponent). As staff type, the
// preview's subject and body targets are rewritten with the {{placeholders}}
// swapped for a sample purchaser's details, and the recipient sentence is
// kept in step with the audience boxes. Every string comes from the server:
// the sample values and the count for each combination of groups are JSON
// data attributes, the sentences are templates, and an empty field shows its
// preview target's data-placeholder instead.
const PLACEHOLDER = /\{\{\s*([a-z_]+)\s*\}\}/g

@controller
class EmailComposerElement extends HTMLElement {
  @target subject!: HTMLInputElement
  @target body!: HTMLTextAreaElement
  @target previewSubject!: HTMLElement
  @target previewBody!: HTMLElement
  @target recipientCount!: HTMLElement
  @targets boxes!: HTMLInputElement[]
  @attr sampleValues = ""
  @attr recipientCounts = ""
  @attr countZero = ""
  @attr countOne = ""
  @attr countOther = ""

  connectedCallback() {
    // A Turbo cache snapshot or browser form restore may have left the
    // fields and the preview out of step.
    this.update()
    this.audienceChanged()
  }

  update() {
    const values = this.parse(this.sampleValues)
    this.fill(this.previewSubject, this.subject.value, values)
    this.fill(this.previewBody, this.body.value, values)
  }

  // The boxes are rendered in ClaimBroadcast::AUDIENCE_KEYS order, which is
  // the order the server joined the keys of each combination in.
  audienceChanged() {
    const keys = this.boxes.filter((box) => box.checked).map((box) => box.value)
    const count = keys.length === 0 ? 0 : Number(this.parse(this.recipientCounts)[keys.join("+")] ?? 0)
    if (keys.length === 0) {
      this.recipientCount.textContent = this.countZero
    } else if (count === 1) {
      this.recipientCount.textContent = this.countOne
    } else {
      this.recipientCount.textContent = this.countOther.replace("%{count}", String(count))
    }
  }

  // Drops the clicked chip's placeholder into the message at the cursor.
  insert(event: Event) {
    const chip = event.currentTarget as HTMLElement
    const token = chip.getAttribute("data-placeholder") || ""
    const start = this.body.selectionStart ?? this.body.value.length
    const end = this.body.selectionEnd ?? start
    this.body.setRangeText(token, start, end, "end")
    this.body.focus()
    this.update()
  }

  private fill(preview: HTMLElement, text: string, values: Record<string, string>) {
    const filled = text.replace(PLACEHOLDER, (match, name: string) => (name in values ? values[name] : match))
    const empty = filled.trim() === ""
    preview.textContent = empty ? preview.getAttribute("data-placeholder") || "" : filled
    preview.classList.toggle("text-gray-900", !empty)
    preview.classList.toggle("text-gray-400", empty)
    preview.classList.toggle("italic", empty)
  }

  private parse(json: string): Record<string, string> {
    try {
      const parsed: unknown = JSON.parse(json || "{}")
      return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, string>) : {}
    } catch {
      return {}
    }
  }
}

export default EmailComposerElement
